import { useRef, useState, type KeyboardEvent, type ReactNode } from "react"
import { Link } from "react-router-dom"
import type { MatterSession } from "../contract"
import { shortDate } from "../answer/format"
import { FEATURE_LABEL, featureOf, type Feature } from "./copy"
import "./matter.css"

type Filter = "all" | Feature

/** Each kind of session opens on its own page. */
const PATH: Record<MatterSession["type"], (id: string) => string> = {
  nira: (id) => `/nira/${encodeURIComponent(id)}`,
  oio: (id) => `/oio-sim/${encodeURIComponent(id)}`,
  oia: (id) => `/oia-sim/${encodeURIComponent(id)}`,
}

const FEATURE_ORDER: Feature[] = ["research", "notice", "appeal", "oio", "oia"]

/** A piece of work that is not a session of its own (a reading of the notice or order), listed beside the sessions. */
export interface ExtraRow { key: string; to: string; title: string; feature: Feature; at: string; line: string; docIds?: string[] }

export interface Row { key: string; to: string; title: string; feature: Feature; at: string; status: string; attention?: boolean; docIds?: string[] }

/** The server's session status in the reader's words. Only what matters is said: done and idle say nothing. */
export function statusWords(status: string): { text: string; attention: boolean } {
  switch (status) {
    case "awaiting_user": return { text: "Needs your input", attention: true }
    case "running": return { text: "Working…", attention: false }
    case "idle": case "done": case "": return { text: "", attention: false }
    default: return { text: status, attention: false }
  }
}

/** The workspace's sessions and readings as rows, each with the documents it was on when known. */
export function rowsOf(sessions: MatterSession[], extra: ExtraRow[] = []): Row[] {
  return [
    ...sessions.map((s) => {
      const w = s.started ? statusWords(s.status) : { text: "Not started", attention: false }
      return { key: `${s.type}-${s.id}`, to: PATH[s.type](s.id), title: s.title, feature: featureOf(s), at: s.updated_at, status: w.text, attention: w.attention, docIds: s.selected_doc_ids ?? undefined }
    }),
    ...extra.map((x) => ({ key: x.key, to: x.to, title: x.title, feature: x.feature, at: x.at, status: x.line, docIds: x.docIds })),
  ]
}

/** Compact session rows: title, type, date, and a status word only when it matters. */
export function SessionRows({ rows, scnOf }: { rows: Row[]; scnOf?: Record<string, string> }) {
  return (
    <ol className="nms-list">
      {[...rows].sort((a, b) => Date.parse(b.at) - Date.parse(a.at)).map((r) => (
        <li key={r.key}>
          <Link className="nms-row" to={r.to}>
            <span className="nms-t">{r.title}</span>
            <span className="nms-sub">
              <span className="nms-tag">{FEATURE_LABEL[r.feature]}</span>
              {scnOf?.[r.key] ? <span className="nms-scn">{scnOf[r.key]}</span> : null}
              <span className="nms-date">{shortDate(r.at)}</span>
              {r.status ? <span className={`nms-status${r.attention ? " attn" : ""}`}>{r.status}</span> : null}
            </span>
          </Link>
        </li>
      ))}
    </ol>
  )
}

/**
 * The sessions a workspace holds, newest first, with a filter by feature (All, then only the features
 * present, each with its count). `total` is the whole count; `action` sits in the header (New session).
 */
export function MatterSessions({ caseId: _caseId, sessions, total: _total, extra = [], action, heading = "Sessions", exclude, scnOf }: {
  caseId: string; sessions: MatterSession[]; total: number; extra?: ExtraRow[]; action?: ReactNode
  /** The section's title ("Sessions", "Across the workspace"). */
  heading?: string
  /** Rows listed elsewhere (on a proceeding): left out here. */
  exclude?: Set<string>
  /** Row key to the short name of the notice it is about, shown as a muted label. */
  scnOf?: Record<string, string>
}) {
  const [filter, setFilter] = useState<Filter>("all")
  const chips = useRef<(HTMLButtonElement | null)[]>([])
  const all = rowsOf(sessions, extra).filter((r) => !exclude?.has(r.key))
  const count = (f: Feature) => all.filter((r) => r.feature === f).length
  const features = FEATURE_ORDER.filter((f) => count(f) > 0)
  const filters: { value: Filter; label: string; n: number }[] = [
    { value: "all", label: "All", n: all.length },
    ...features.map((f) => ({ value: f, label: FEATURE_LABEL[f], n: count(f) })),
  ]
  // A filter earns its place only when there is enough to filter and more than one kind.
  const filterable = all.length >= 4 && features.length >= 2
  const shown = all
    .filter((r) => filter === "all" || r.feature === filter)
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))

  // Radio-group keys: arrows move and select with wrap-around, Home and End jump to the ends.
  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    const last = filters.length - 1
    const at = filters.findIndex((f) => f.value === filter)
    const next: Record<string, number> = {
      ArrowLeft: at === 0 ? last : at - 1,
      ArrowUp: at === 0 ? last : at - 1,
      ArrowRight: at === last ? 0 : at + 1,
      ArrowDown: at === last ? 0 : at + 1,
      Home: 0,
      End: last,
    }
    const to = next[e.key]
    if (to === undefined) return
    e.preventDefault()
    setFilter(filters[to].value)
    chips.current[to]?.focus()
  }

  return (
    <section className="nms" aria-labelledby="nms-head">
      {heading || action ? (
        <div className="nms-head">
          <h2 id="nms-head" className="nms-h">{heading} <span className="nms-count">{all.length}</span></h2>
          {action}
        </div>
      ) : null}
      {filterable ? (
        <div className="nms-filters" role="radiogroup" aria-label="Filter sessions by feature" onKeyDown={onKey}>
          {filters.map((f, i) => (
            <button
              key={f.value}
              ref={(el) => { chips.current[i] = el }}
              type="button"
              role="radio"
              aria-checked={filter === f.value}
              tabIndex={filter === f.value ? 0 : -1}
              className={`nms-filter${filter === f.value ? " on" : ""}`}
              onClick={() => setFilter(f.value)}
            >
              {f.label}<span className="nms-n">{f.n}</span>
            </button>
          ))}
        </div>
      ) : null}

      {all.length === 0 ? (
        <p className="nms-empty">Nothing started in this workspace yet.</p>
      ) : shown.length === 0 ? (
        <p className="nms-empty">No sessions from this feature in this workspace.</p>
      ) : (
        <SessionRows rows={shown} scnOf={scnOf} />
      )}
    </section>
  )
}
